import { useState } from 'react'
import { useAuth } from '../lib/auth.jsx'
import { AVATAR_PALETTE } from '../lib/format.js'
import { Button, Field, Page, PageHeader } from '../ds/index.js'
import Avatar from './Avatar.jsx'
import GoogleSettingsSection from './GoogleSettingsSection.jsx'
import EmailTemplatesSection from './EmailTemplatesSection.jsx'
import StartupSection from './StartupSection.jsx'

const sectionLabel = { color: 'var(--color-gold)' }

function SettingsPageInner({ user, updateProfile, signOut, workspaceDomain, theme, onToggleTheme, archivedProjects = [], onRestoreProject, emailTemplates = [], onCreateTemplate, onUpdateTemplate, onDeleteTemplate }) {
  const [name, setName] = useState(user.name || '')
  const [saving, setSaving] = useState(false)
  const dirty = name.trim() && name.trim() !== user.name
  const saveName = async () => {
    if (!dirty || saving) return
    setSaving(true)
    try { await updateProfile({ name: name.trim() }) } finally { setSaving(false) }
  }
  const pickColor = (c) => updateProfile({ color: c.color, ink: c.ink })
  const row = { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }

  return (
    <Page>
      <PageHeader title="Settings" />
      {/* identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
        <Avatar user={user} size={48} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-white)' }}>{user.name}</div>
          <div className="font-mono" style={{ fontSize: 12, color: 'var(--color-muted)' }}>{user.email}</div>
        </div>
      </div>

      {/* Profile: display name */}
      <div className="eyebrow" style={{ marginBottom: 10 }}><span className="tag-label" style={sectionLabel}>Profile</span></div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Field label="Display name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveName()} />
        </div>
        <Button onClick={saveName} disabled={!dirty || saving}>Save</Button>
      </div>

      {/* Profile: avatar color */}
      <div style={{ marginBottom: 18 }}>
        <span className="tag-label" style={{ color: 'var(--color-muted)', display: 'block', marginBottom: 8 }}>Avatar color</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {AVATAR_PALETTE.map((c) => {
            const on = c.color === user.color
            return (
              <button key={c.color} onClick={() => pickColor(c)} title={c.color}
                style={{ width: 30, height: 30, borderRadius: '50%', background: c.color, color: c.ink, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', outline: on ? '2px solid var(--color-gold)' : '2px solid transparent', outlineOffset: 2, fontSize: 12, fontWeight: 600 }}>
                {on ? user.initials : ''}
              </button>
            )
          })}
        </div>
      </div>

      {/* Appearance */}
      <div className="eyebrow" style={{ marginBottom: 10 }}><span className="tag-label" style={sectionLabel}>Appearance</span></div>
      <div style={{ ...row, justifyContent: 'space-between', borderTop: '1px solid var(--color-divider-dark)' }}>
        <span style={{ fontSize: 13, color: 'var(--color-white)' }}>Theme</span>
        <Button variant="ghost" size="sm" onClick={onToggleTheme}>{theme === 'light' ? 'Switch to dark' : 'Switch to light'}</Button>
      </div>

      {/* Startup */}
      <StartupSection user={user} updateProfile={updateProfile} />

      {/* Google Workspace */}
      <GoogleSettingsSection user={user} updateProfile={updateProfile} />

      {/* Canned email templates (register item 9) */}
      <div className="eyebrow" style={{ margin: '16px 0 10px' }}><span className="tag-label" style={sectionLabel}>Email</span></div>
      <EmailTemplatesSection
        templates={emailTemplates}
        onCreate={onCreateTemplate}
        onUpdate={onUpdateTemplate}
        onDelete={onDeleteTemplate}
      />

      {/* Archived projects */}
      {archivedProjects.length > 0 && (
        <>
          <div className="eyebrow" style={{ margin: '16px 0 10px' }}><span className="tag-label" style={sectionLabel}>Archived projects</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {archivedProjects.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--color-divider-dark)' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--color-white)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                <Button variant="ghost" size="sm" onClick={() => onRestoreProject(p.id)}>Restore</Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Account */}
      <div className="eyebrow" style={{ margin: '16px 0 10px' }}><span className="tag-label" style={sectionLabel}>Account</span></div>
      <div style={{ ...row, justifyContent: 'space-between', borderTop: '1px solid var(--color-divider-dark)' }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--color-white)' }}>Signed in via Google Workspace</div>
          {workspaceDomain && <div className="font-mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>@{workspaceDomain}</div>}
        </div>
        <Button variant="ghost" size="sm" onClick={signOut}>Sign out</Button>
      </div>
    </Page>
  )
}

export default function SettingsPage({ archivedProjects, onRestoreProject, theme, onToggleTheme, emailTemplates, onCreateTemplate, onUpdateTemplate, onDeleteTemplate }) {
  const auth = useAuth()
  return (
    <SettingsPageInner
      user={auth.user} updateProfile={auth.updateProfile} signOut={auth.signOut} workspaceDomain={auth.workspaceDomain}
      theme={theme} onToggleTheme={onToggleTheme} archivedProjects={archivedProjects} onRestoreProject={onRestoreProject}
      emailTemplates={emailTemplates} onCreateTemplate={onCreateTemplate} onUpdateTemplate={onUpdateTemplate} onDeleteTemplate={onDeleteTemplate}
    />
  )
}
